import { gql } from '@/lib/api';
import { toCsv } from '@/lib/csv';

/**
 * OS-1723 — precios por módulo y suscripción del tenant. Los montos son mensuales en COP. Qué
 * puede hacer cada rol (editar la lista, tope de descuento) lo decide el API y llega en la
 * respuesta (`canEdit`, `discountLimitPct`); la consola no repite la regla.
 */

/** `real` (activo, mora) | `potential` (piloto, prueba) | `none` (suspendido, cancelado). */
export type MrrKind = 'real' | 'potential' | 'none';

export interface ModulePrice {
  moduleKey: string;
  name: string;
  isCore: boolean;
  currency: string;
  amountMonthly: number | null;
  validFrom: string | null;
}

export interface PriceList {
  currency: string;
  canEdit: boolean;
  modules: ModulePrice[];
}

export interface SubscriptionItem {
  id: string;
  moduleKey: string;
  moduleName: string;
  currency: string;
  unitPrice: number;
  discountPct: number;
  discountReason: string | null;
  monthlyAmount: number;
  listPrice: number | null;
  startsAt: string;
  endsAt: string | null;
  /** `module_disabled` | `manual`. */
  endReason: string | null;
}

export interface BillableModule {
  moduleKey: string;
  name: string;
  isCore: boolean;
  listPrice: number | null;
}

export interface TenantSubscription {
  tenantId: string;
  planStatus: string;
  currency: string;
  mrrKind: MrrKind;
  mrr: number;
  potentialMrr: number;
  contractedMrr: number;
  items: SubscriptionItem[];
  endedItems: SubscriptionItem[];
  billableModules: BillableModule[];
  discountLimitPct: number;
}

export interface SubscriptionExportRow {
  tenantId: string;
  tenantName: string;
  planStatus: string;
  mrrKind: MrrKind;
  moduleKey: string;
  currency: string;
  unitPrice: number;
  discountPct: number;
  monthlyAmount: number;
  startsAt: string;
}

export const SUBSCRIPTION_REASON_LIMITS = { minLength: 10, maxLength: 500 } as const;

export const tenantSubscriptionQueryKey = (tenantId: string): readonly string[] => [
  'platform-tenants',
  'subscription',
  tenantId,
];
export const MODULE_PRICES_QUERY_KEY = ['platform-module-prices'] as const;

const ITEM_FIELDS = `id moduleKey moduleName currency unitPrice discountPct discountReason
  monthlyAmount listPrice startsAt endsAt endReason`;
const SUBSCRIPTION_FIELDS = `tenantId planStatus currency mrrKind mrr potentialMrr contractedMrr
  discountLimitPct items { ${ITEM_FIELDS} } endedItems { ${ITEM_FIELDS} }
  billableModules { moduleKey name isCore listPrice }`;
const PRICE_LIST_FIELDS = `currency canEdit modules { moduleKey name isCore currency amountMonthly validFrom }`;

/** `platform.billing.read`. */
export async function getTenantSubscription(tenantId: string): Promise<TenantSubscription> {
  const data = await gql<{ platformTenantSubscription: TenantSubscription }>(
    `query TenantSubscription($tenantId: ID!) {
      platformTenantSubscription(tenantId: $tenantId) { ${SUBSCRIPTION_FIELDS} }
    }`,
    { tenantId },
  );
  return data.platformTenantSubscription;
}

/** D1A — agrega un módulo habilitado a la suscripción con el precio de lista vigente. */
export async function addTenantSubscriptionItem(
  tenantId: string,
  moduleKey: string,
): Promise<TenantSubscription> {
  const data = await gql<{ addTenantSubscriptionItem: TenantSubscription }>(
    `mutation AddTenantSubscriptionItem($tenantId: ID!, $moduleKey: String!) {
      addTenantSubscriptionItem(tenantId: $tenantId, moduleKey: $moduleKey) { ${SUBSCRIPTION_FIELDS} }
    }`,
    { tenantId, moduleKey },
  );
  return data.addTenantSubscriptionItem;
}

export interface UpdateSubscriptionItemInput {
  discountPct?: number;
  applyListPrice?: boolean;
  reason: string;
}

export async function updateTenantSubscriptionItem(
  tenantId: string,
  itemId: string,
  input: UpdateSubscriptionItemInput,
): Promise<TenantSubscription> {
  const data = await gql<{ updateTenantSubscriptionItem: TenantSubscription }>(
    `mutation UpdateTenantSubscriptionItem($tenantId: ID!, $itemId: ID!, $input: UpdateTenantSubscriptionItemInput!) {
      updateTenantSubscriptionItem(tenantId: $tenantId, itemId: $itemId, input: $input) { ${SUBSCRIPTION_FIELDS} }
    }`,
    { tenantId, itemId, input },
  );
  return data.updateTenantSubscriptionItem;
}

export async function endTenantSubscriptionItem(
  tenantId: string,
  itemId: string,
  reason: string,
): Promise<TenantSubscription> {
  const data = await gql<{ endTenantSubscriptionItem: TenantSubscription }>(
    `mutation EndTenantSubscriptionItem($tenantId: ID!, $itemId: ID!, $reason: String!) {
      endTenantSubscriptionItem(tenantId: $tenantId, itemId: $itemId, reason: $reason) { ${SUBSCRIPTION_FIELDS} }
    }`,
    { tenantId, itemId, reason },
  );
  return data.endTenantSubscriptionItem;
}

export async function getModulePrices(): Promise<PriceList> {
  const data = await gql<{ platformModulePrices: PriceList }>(
    `query ModulePrices { platformModulePrices { ${PRICE_LIST_FIELDS} } }`,
  );
  return data.platformModulePrices;
}

/** Solo SUPER (lo valida el API). `null` deja el módulo sin precio de lista. */
export async function setModulePrice(
  moduleKey: string,
  amountMonthly: number | null,
): Promise<PriceList> {
  const data = await gql<{ setPlatformModulePrice: PriceList }>(
    `mutation SetModulePrice($moduleKey: String!, $amountMonthly: Float) {
      setPlatformModulePrice(moduleKey: $moduleKey, amountMonthly: $amountMonthly) { ${PRICE_LIST_FIELDS} }
    }`,
    { moduleKey, amountMonthly },
  );
  return data.setPlatformModulePrice;
}

export async function getSubscriptionExport(): Promise<SubscriptionExportRow[]> {
  const data = await gql<{ platformSubscriptionExport: SubscriptionExportRow[] }>(
    `query SubscriptionExport { platformSubscriptionExport {
      tenantId tenantName planStatus mrrKind moduleKey currency unitPrice discountPct monthlyAmount startsAt
    } }`,
  );
  return data.platformSubscriptionExport;
}

const CSV_HEADER = [
  'tenant_id',
  'tenant',
  'estado',
  'aporte',
  'modulo',
  'moneda',
  'precio_mensual',
  'descuento_pct',
  'valor_mensual',
  'desde',
];

/** CSV de suscripciones (una fila por línea vigente). Montos con punto decimal, sin símbolo. */
export function subscriptionsToCsv(rows: SubscriptionExportRow[]): string {
  return toCsv(
    CSV_HEADER,
    rows.map((row) => [
      row.tenantId,
      row.tenantName,
      row.planStatus,
      row.mrrKind,
      row.moduleKey,
      row.currency,
      row.unitPrice.toFixed(2),
      row.discountPct.toFixed(2),
      row.monthlyAmount.toFixed(2),
      row.startsAt.slice(0, 10),
    ]),
  );
}
